import * as React from "react"

import userService from "@services/userService/userService.ts"

import { StreamChat } from "stream-chat"
import { AppFunctions } from "@others/AppFunctions.ts"
import { AppStreamChatGenerics } from "@interfaces/IAppStreamChatGenerics.ts"

const BACKOFFICE_USER_ID = AppFunctions.getViteEnv(
		"VITE_STREAM_BACKOFFICE_USER_ID"
	),
	BACKOFFICE_USER_NAME = "Distrito Globo",
	BACKOFFICE_USER_IMAGE =
		"https://membership.elglobodistrict.com/assets/images/globo_backoffice_avatar.jpg"

export type IChatBootstrapStatus = "idle" | "loading" | "ready" | "error"

interface IOutput {
	chatClient?: StreamChat<AppStreamChatGenerics>
	status: IChatBootstrapStatus
	initChat: () => Promise<void>
}

let sharedClient: StreamChat<AppStreamChatGenerics> | undefined
let sharedStatus: IChatBootstrapStatus = "idle"
let sharedInitPromise: Promise<void> | undefined
let sharedDisconnectPromise: Promise<void> | undefined
let subscriberCount = 0
let pendingDisconnectTimer: ReturnType<typeof setTimeout> | undefined

const clearPendingDisconnectTimer = () => {
	if (!pendingDisconnectTimer) return

	clearTimeout(pendingDisconnectTimer)
	pendingDisconnectTimer = undefined
}

const disconnectClient = async (
	client?: StreamChat<AppStreamChatGenerics>
) => {
	if (!client) return

	try {
		await client.disconnectUser()
	} catch (error) {
		console.error("Error disconnecting backoffice chat client:", error)
	}
}

const queueSharedDisconnect = (client?: StreamChat<AppStreamChatGenerics>) => {
	const disconnectTask = disconnectClient(client).finally(() => {
		if (sharedDisconnectPromise === disconnectTask) {
			sharedDisconnectPromise = undefined
		}
	})

	sharedDisconnectPromise = disconnectTask

	return disconnectTask
}

const useStreamChatBackofficeData = (): IOutput => {
	const mountedRef = React.useRef(true)

	const [chatClient, setChatClient] = React.useState<
		StreamChat<AppStreamChatGenerics> | undefined
	>(() => sharedClient)
	const [status, setStatus] = React.useState<IChatBootstrapStatus>(
		() => sharedStatus
	)

	const syncFromSharedState = React.useCallback(() => {
		if (!mountedRef.current) return

		setChatClient(sharedClient)
		setStatus(sharedStatus)
	}, [])

	const initChat = React.useCallback(async () => {
		clearPendingDisconnectTimer()

		if (sharedInitPromise && sharedStatus === "loading") {
			syncFromSharedState()
			await sharedInitPromise
			syncFromSharedState()
			return
		}

		if (sharedStatus === "ready" && sharedClient) {
			syncFromSharedState()
			return
		}

		sharedStatus = "loading"
		syncFromSharedState()

		let currentInitPromise: Promise<void> | undefined

		currentInitPromise = (async () => {
			if (sharedDisconnectPromise) {
				await sharedDisconnectPromise
			}

			const nextClient = StreamChat.getInstance<AppStreamChatGenerics>(
				AppFunctions.getViteEnv("VITE_STREAM_API_KEY")
			)

			try {
				await nextClient.connectUser(
					{
						id: BACKOFFICE_USER_ID,
						name: BACKOFFICE_USER_NAME,
						image: BACKOFFICE_USER_IMAGE,
					},
					async () => {
						if (AppFunctions.getViteEnv("VITE_USE_MOCKS")) {
							return AppFunctions.getViteEnv(
								"VITE_STREAM_BACKOFFICE_USER_TOKEN"
							)
						}

						const { token } = await userService.getUserChatToken()
						return token || ""
					}
				)

				if (subscriberCount === 0) {
					sharedClient = undefined
					sharedStatus = "idle"
					await queueSharedDisconnect(nextClient)
					syncFromSharedState()
					return
				}

				sharedClient = nextClient
				sharedStatus = "ready"
				syncFromSharedState()
			} catch (error) {
				sharedClient = undefined
				sharedStatus = subscriberCount > 0 ? "error" : "idle"
				await queueSharedDisconnect(nextClient)
				syncFromSharedState()

				console.error("Error initializing backoffice chat client:", error)
			} finally {
				if (sharedInitPromise === currentInitPromise) {
					sharedInitPromise = undefined
				}
			}
		})()

		sharedInitPromise = currentInitPromise

		await currentInitPromise
	}, [syncFromSharedState])

	React.useEffect(() => {
		mountedRef.current = true
		subscriberCount += 1
		clearPendingDisconnectTimer()
		syncFromSharedState()
		void initChat()

		return () => {
			mountedRef.current = false
			subscriberCount = Math.max(0, subscriberCount - 1)

			if (subscriberCount > 0) return

			pendingDisconnectTimer = setTimeout(() => {
				pendingDisconnectTimer = undefined

				if (subscriberCount > 0 || sharedInitPromise) return

				const connectedClient = sharedClient

				sharedClient = undefined
				sharedStatus = "idle"

				if (connectedClient) {
					void queueSharedDisconnect(connectedClient)
				}
			}, 0)
		}
	}, [initChat, syncFromSharedState])

	return {
		chatClient,
		status,
		initChat,
	}
}

export default useStreamChatBackofficeData
